import { useQuery } from "@tanstack/react-query";
import { Coins } from "lucide-react";
import { Link } from "wouter";
import { useUsageLimits } from "@/hooks/use-usage-limits";
import { useAuth } from "@/hooks/use-auth";

interface CreditBalanceResponse {
  credits: number;
  nextRefillDate: string | null;
}

/**
 * Amber nav pill showing credit balance when < 5 credits.
 * Hidden when loading or balance >= 5 (no flicker).
 * Shows "0 . Nd" when balance is 0, where N = days until next refill.
 */
export function CreditBalancePill() {
  const { isLoading, creditBalance } = useUsageLimits();
  const { isAuthenticated } = useAuth();

  // Only fetch refill date when balance is 0 and user is authenticated
  const { data: balanceData } = useQuery<CreditBalanceResponse>({
    queryKey: ["/api/credits/balance"],
    enabled: isAuthenticated && !isLoading && creditBalance === 0,
  });

  // Never render while loading (prevents amber flicker on load)
  if (isLoading) return null;

  // Hidden when balance is healthy (>= 5)
  if (creditBalance >= 5) return null;

  const renderBalance = () => {
    if (creditBalance === 0 && balanceData?.nextRefillDate) {
      const now = Date.now();
      const refillMs = new Date(balanceData.nextRefillDate).getTime();
      const daysLeft = Math.max(1, Math.ceil((refillMs - now) / (1000 * 60 * 60 * 24)));
      return (
        <>
          <span>0</span>
          <span className="mx-0.5 opacity-50">·</span>
          <span>{daysLeft}d</span>
        </>
      );
    }
    return <span>{creditBalance}</span>;
  };

  return (
    <Link
      href="/settings"
      data-testid="credit-balance-pill"
      className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200 no-underline hover:bg-amber-200 dark:hover:bg-amber-900/50 transition-colors"
    >
      <Coins className="h-3 w-3 shrink-0" aria-hidden="true" />
      {renderBalance()}
    </Link>
  );
}
